import React, {
  ReactNode,
  useEffect,
  useRef,
  CSSProperties,
  Children,
  isValidElement,
  cloneElement,
  Fragment,
} from "react";
import { useWizardContext } from "@shared/contexts/WizardContext/WizardContext";
import useMountEffect from "@shared/hooks/useMountEffect/useMountEffect";
import type { BoxSharedProps } from "@shared/components/ui-kit/layout/Box/Box.props";
import { noop } from "lodash";

export interface StepProps extends Omit<BoxSharedProps, "children"> {
  stepId: string;
  stepName: string;
  children:
    | ((args: {
        onValid: () => void;
        onInvalid: () => void;
        style?: React.CSSProperties;
      }) => ReactNode)
    | ReactNode;

  /**
   * Whether this step should asynchronously register to the wizard.
   */
  async?: boolean; // eslint-disable-line react/no-unused-prop-types -- false positive
  /**
   * Whether this step should register to the wizard.
   */
  shouldRegister?: boolean; // eslint-disable-line react/no-unused-prop-types -- false positive
  /**
   * Whether this step is ready to be displayed.
   */
  isReady?: boolean; // eslint-disable-line react/no-unused-prop-types -- false positive
  /**
   * Whether this step is valid. You can use this prop if you dont need to trigger onValid/onInvalid programatically.
   * It's usually better to use react decleratively than imperatively.
   */
  isValid?: boolean | null;
  defaultIsValid?: boolean;
  /**
   * Whether a step is hidden to data logic or not.
   * @see {Step['hidden']} from useWizard
   *
   * This is useful for metadata-steps like loaders, error and success UIs. This however
   * should not be used to enable/disable steps. To conditionally enable a step, look into
   * conditionally registering the step. To conditionally disable a step, remove
   * the step from the wizard completely.
   */
  hidden?: boolean;
  /**
   * Determines if this step should stay mounted once it has mounted at least once.
   *
   * When this is true, the immediate child is passed a style prop that contains the CSS styling to keep an element mounted but hidden from visual and accessibility cues.
   */
  keepMounted?: boolean;

  // eslint-disable-next-line
  customData?: Record<string, any>;
}

// eslint-disable-next-line
export function WizardStep({ async = false, ...props }: StepProps) {
  const Component = async ? WizardStep.Async : WizardStep.Sync;
  return <Component {...props} />;
}

const isElementWithStyleAndChildren = (
  element: React.ReactElement
): element is React.ReactElement<{
  style: CSSProperties;
  children: ReactNode;
}> => {
  return "style" in element.props && "children" in element.props;
};

const hideButKeepMounted = (content: ReactNode) => {
  const keepMountedStyle: CSSProperties = {
    opacity: 0,
    width: "0",
    height: "0",
    flex: 0,
    position: "absolute",
    overflow: "hidden",
  };

  // We cannot just wrap the content in a hidden div because it causes unnecessary unmount/mount when we unwrap the div.
  // If we were to add a div in the base case as well, that would fix the unmount/mount issue but breaks the layout because
  // it expects two children.
  if (isValidElement(content) && isElementWithStyleAndChildren(content)) {
    if (content.type === Fragment) {
      return cloneElement(
        content,
        {},
        // Map through Fragment children to apply style to each valid element
        Children.map(content.props.children, (child) =>
          // Check if child is a valid React element before cloning
          isValidElement(child) && isElementWithStyleAndChildren(child)
            ? cloneElement(child, {
                style: keepMountedStyle,
              })
            : child
        )
      );
    }
    // For non-Fragment elements, clone and apply style directly
    return cloneElement(content, {
      style: keepMountedStyle,
    });
  }

  return null;
};

WizardStep.Sync = function WizardStepSync({
  stepId,
  stepName,
  children,
  shouldRegister = true,
  isReady = true,
  defaultIsValid = true,
  isValid = null,
  hidden = false,
  keepMounted = false,
  customData,
}: StepProps) {
  const {
    currentStep,
    register,
    unregister,
    saveStepOrder,
    hasStep,
    updateSteps,
  } = useWizardContext();
  const onValidRef = useRef<() => void>(noop);
  const onInvalidRef = useRef<() => void>(noop);
  const hasMountedOnceRef = useRef(false);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (typeof isValid === "boolean") {
      updateSteps({
        [stepId]: { isValid },
      });
    }
  }, [isValid, stepId, updateSteps]);

  useMountEffect(() => {
    saveStepOrder?.(stepId);

    if (shouldRegister) {
      const { onValid, onInvalid } = register(() => {
        return {
          id: stepId,
          name: stepName,
          isValid: isValid ?? defaultIsValid,
          hidden,
          keepMounted,
          isReady,
          customData,
        };
      });
      onValidRef.current = onValid;
      onInvalidRef.current = onInvalid;
    }
    isFirstRender.current = false;
  });

  // Handles registering or unregistering a step that dynamically should be
  // registered or unregistered after the wizard is ready
  useEffect(() => {
    if (!shouldRegister) {
      if (hasStep(stepId)) {
        unregister(stepId);
      }
      return;
    }

    const stepExists = hasStep(stepId);

    if (!stepExists && !isFirstRender.current) {
      const { onValid, onInvalid } = register(
        () => {
          return {
            id: stepId,
            name: stepName,
            isValid: isValid ?? defaultIsValid,
            hidden,
            keepMounted,
            isReady,
            customData,
          };
        },
        { shouldRegisterIntoPosition: true }
      );
      onValidRef.current = onValid;
      onInvalidRef.current = onInvalid;
    }
    // otherwise step would have been registered on mount
  }, [stepId, shouldRegister, unregister]);

  const isActive = currentStep?.id === stepId;

  // Track first mount when step becomes active
  if (isActive) {
    hasMountedOnceRef.current = true;
  }

  const content =
    typeof children === "function"
      ? children({
          onValid: onValidRef.current,
          onInvalid: onInvalidRef.current,
        })
      : children;

  if (!isActive) {
    if (keepMounted && hasMountedOnceRef.current) {
      return hideButKeepMounted(content);
    }
    return null;
  }

  return <>{content}</>;
};

WizardStep.Async = function WizardStepAsync({
  stepId,
  stepName,
  children,
  shouldRegister = true,
  isReady = false,
  defaultIsValid = true,
  isValid = null,
  hidden = false,
  keepMounted = false,
  customData,
}: StepProps) {
  const {
    currentStep,
    register,
    unregister,
    updateSteps,
    saveStepOrder,
    hasStep,
    steps,
  } = useWizardContext();
  const onValidRef = useRef<() => void>(noop);
  const onInvalidRef = useRef<() => void>(noop);
  const hasMountedOnceRef = useRef(false);
  const isFirstRender = useRef(true);

  useMountEffect(() => {
    saveStepOrder?.(stepId);

    // Always register on mount with `isReady` set to false
    const { onValid, onInvalid } = register(() => {
      return {
        id: stepId,
        name: stepName,
        isValid: isValid ?? defaultIsValid,
        hidden,
        keepMounted,
        isReady: false,
        customData,
      };
    });

    onValidRef.current = onValid;
    onInvalidRef.current = onInvalid;

    isFirstRender.current = false;
  });

  useEffect(() => {
    if (!shouldRegister) {
      if (hasStep(stepId)) {
        unregister(stepId);
      }
      return;
    }

    if (isReady) {
      const stepExists = hasStep(stepId);

      // Handles registering a step that dynamically should be registered after the wizard is ready
      if (!stepExists && !isFirstRender.current) {
        const { onValid, onInvalid } = register(
          () => {
            return {
              id: stepId,
              name: stepName,
              isValid: isValid ?? defaultIsValid,
              hidden,
              keepMounted,
              isReady,
              customData,
            };
          },
          { shouldRegisterIntoPosition: true }
        );
        onValidRef.current = onValid;
        onInvalidRef.current = onInvalid;
      } else {
        const isRegisteredReady = steps.find((s) => s.id === stepId)?.isReady;
        if (!isRegisteredReady) {
          updateSteps({
            [stepId]: {
              isReady: true,
            },
          });
        }
      }
    }
  }, [isReady, stepId, shouldRegister, unregister, hasStep, steps]);

  useEffect(() => {
    if (typeof isValid === "boolean") {
      updateSteps({
        [stepId]: { isValid },
      });
    }
  }, [isValid, stepId, updateSteps]);

  const isActive = currentStep?.id === stepId;

  // Track first mount when step becomes active
  if (isActive) {
    hasMountedOnceRef.current = true;
  }

  const content =
    typeof children === "function"
      ? children({
          onValid: onValidRef.current,
          onInvalid: onInvalidRef.current,
        })
      : children;

  if (!isActive) {
    if (keepMounted && hasMountedOnceRef.current) {
      return hideButKeepMounted(content);
    }
    return null;
  }

  return <>{content}</>;
};
